import { setAcknowledgedRouteIntercept } from "../../../redux/slices/applicationSlice";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { usePostRouteQuery } from "../../../redux/services/vectormapsAPI";
import { usePostAnalyticsEvent } from "../../../hooks/usePostAnalyticsEvent";

import { QueryParamBinary, QueryParams } from "../../../utils/types";
import { AnalyticsEvent } from "../../../utils/analyticsTypes";
import { buildRouteData } from "../../../utils/buildRouteData";

import { FloorConfig } from "../../../components/Map/plugins/floor-control";

const isValidRouteEndpoint = (endpoint?: string): boolean => {
  // Check if endpoint is defined and not empty placeholders
  if (!endpoint || endpoint === "" || endpoint === "-" || endpoint === "''") {
    return false;
  }

  // Check if it's a coordinate pair (lng,lat)
  const splitEndpoint = endpoint.split(",");
  if (splitEndpoint.length === 2) {
    const [lng, lat] = splitEndpoint;
    const lngNum = Number(lng);
    const latNum = Number(lat);

    // Validate both coordinates are valid numbers
    return (
      !isNaN(lngNum) &&
      !isNaN(latNum) &&
      lngNum !== 0 &&
      latNum !== 0 && // Basic sanity check for non-zero coordinates
      Math.abs(lngNum) <= 180 &&
      Math.abs(latNum) <= 90
    ); // Valid coordinate ranges
  }

  // For POI IDs or LM IDs, just check they're not empty and contain valid characters
  return endpoint.trim().length > 0;
};

export const usePostRouteData = ({
  fromId,
  toId,
  floor,
}: {
  fromId?: string;
  toId?: string;
  floor?: FloorConfig;
}) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { logAnalyticsEvent } = usePostAnalyticsEvent();

  const { project } = useAppSelector((state) => state.application);

  const stepFreeRoute =
    searchParams.get(QueryParams.STEP_FREE) === QueryParamBinary.ENABLED;

  const splitFromId = fromId?.split(",");
  const splitToId = toId?.split(",");

  // to maintain backwards compatibility check if numbers and use old endpoint
  const re = /^[0-9]+$/;
  const version = re.test(fromId!) && re.test(toId!) ? "v1" : "v2";
  const fromObj = re.test(fromId!)
    ? { featureId: Number(fromId) }
    : { lmId: fromId };
  const toObj = re.test(toId!) ? { featureId: Number(toId) } : { lmId: toId };

  const { currentData, isFetching, isError, isSuccess } = usePostRouteQuery(
    {
      version,
      from:
        splitFromId?.length === 2
          ? {
              longitude: Number(splitFromId[0]),
              latitude: Number(splitFromId[1]),
              floorId: floor?.id || 0,
            }
          : fromObj,
      to:
        splitToId?.length === 2
          ? {
              longitude: Number(splitToId[0]),
              latitude: Number(splitToId[1]),
              floorId: floor?.id || 0,
            }
          : toObj,
      ...(stepFreeRoute ? { options: { routeModifier: "step_free" } } : {}),
      project,
    },
    {
      skip:
        !isValidRouteEndpoint(fromId) ||
        !isValidRouteEndpoint(toId) ||
        !floor?.id ||
        !project,
    },
  );

  useEffect(() => {
    if (!fromId || !toId) return;
    if (!isFetching && !isError) return;

    const { fromData, toData } = buildRouteData({ fromId, toId, floor });

    const eventData = {
      ...fromData,
      ...toData,
      step_free: stepFreeRoute,
    };

    if (isFetching) {
      logAnalyticsEvent({
        event_type: AnalyticsEvent.ROUTE_REQUEST,
        event_data: eventData,
      });
    } else if (isError) {
      logAnalyticsEvent({
        event_type: AnalyticsEvent.ROUTE_FAIL,
        event_data: eventData,
      });
    }
  }, [
    isFetching,
    isError,
    fromId,
    toId,
    floor,
    logAnalyticsEvent,
    stepFreeRoute,
  ]);

  // Never present a previous request's interception while a new route is loading.
  const data = !isFetching && !isError ? currentData : undefined;
  const routeRequestKey = JSON.stringify([
    project,
    fromId,
    toId,
    stepFreeRoute,
    fromId?.includes(",") || toId?.includes(",") ? floor?.id : null,
  ]);
  useEffect(() => {
    if (!stepFreeRoute || isError || (data && !data.intercept)) {
      dispatch(setAcknowledgedRouteIntercept(undefined));
    }
  }, [dispatch, stepFreeRoute, isError, data]);

  return {
    data,
    isFetching,
    isError,
    isSuccess: isSuccess && !!data,
    stepFreeRoute,
    routeRequestKey,
  };
};
